import { useState } from 'react';
import { useLocation } from 'wouter';
import { ArrowLeft, Check, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { GameSettings, DEFAULT_SETTINGS, AGE_GROUP_PRESETS } from '@/types/game';
import { STORAGE_KEYS } from '@/lib/constants';
import { playNotification, initAudioContext } from '@/lib/notifications';

const TEAM_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Brown', value: '#a16207' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'Black', value: '#1f2937' },
  { name: 'White', value: '#f3f4f6' },
  { name: 'Gold', value: '#fbbf24' },
];

function SettingsView() {
  const [, setLocation] = useLocation();
  const [settings, setSettings] = useState<GameSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure backward compatibility - add missing fields
        return {
          ...DEFAULT_SETTINGS,
          ...parsed,
          notificationType: parsed.notificationType || 'siren',
          halfTimeBreakDuration: parsed.halfTimeBreakDuration || 5,
          theme: parsed.theme || 'green'
        };
      }
      return DEFAULT_SETTINGS;
    } catch (err) {
      console.error('Failed to load settings:', err);
      return DEFAULT_SETTINGS;
    }
  });

  const handleAgeGroupChange = (ageGroup: string) => {
    const preset = AGE_GROUP_PRESETS[ageGroup];
    if (preset) {
      setSettings(prev => ({
        ...prev,
        ageGroup,
        ...preset,
      }));
    } else {
      setSettings(prev => ({ ...prev, ageGroup }));
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
      toast.success('Settings saved!');
      setLocation('/');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings. Please try again.');
    }
  };

  const handleTestNotification = () => {
    initAudioContext();
    playNotification(settings.notificationType);
    toast.success('Test notification played!');
  };

  const selectedHomeColor = TEAM_COLORS.find(c => c.value === settings.homeTeamColor);
  const selectedAwayColor = TEAM_COLORS.find(c => c.value === settings.awayTeamColor);

  return (
    <div className="min-h-[100dvh] p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setLocation('/')}
            className="bg-on-dark-10 border-on-dark-20 text-on-dark hover:bg-on-dark-20"
            aria-label="Back to game"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold text-on-dark" style={{ fontFamily: 'Teko, "Teko Fallback", sans-serif' }}>SETTINGS</h1>
        </div>

        <Card className="p-6 bg-card space-y-6">
          {/* Age Group Preset */}
          <div className="space-y-2">
            <Label htmlFor="age-group">Age Group</Label>
            <Select value={settings.ageGroup} onValueChange={handleAgeGroupChange}>
              <SelectTrigger id="age-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(AGE_GROUP_PRESETS).map(group => (
                  <SelectItem key={group} value={group}>
                    {group}
                  </SelectItem>
                ))}
                <SelectItem value="Custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Half Duration */}
          <div className="space-y-2">
            <Label htmlFor="half-duration">Half Duration (minutes)</Label>
            <Input
              id="half-duration"
              type="number"
              min="1"
              max="90"
              value={settings.halfDuration || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setSettings(prev => ({ ...prev, halfDuration: 0 }));
                } else {
                  const num = parseInt(value);
                  if (!isNaN(num) && num >= 0 && num <= 90) {
                    setSettings(prev => ({ ...prev, halfDuration: num }));
                  }
                }
              }}
              onBlur={() => {
                if (!settings.halfDuration || settings.halfDuration < 1) {
                  setSettings(prev => ({ ...prev, halfDuration: 35 }));
                }
              }}
              placeholder="Enter duration"
              aria-label="Half duration in minutes"
            />
          </div>

          {/* Half-Time Break Duration */}
          <div className="space-y-2">
            <Label htmlFor="break-duration">Half-Time Break (minutes)</Label>
            <Input
              id="break-duration"
              type="number"
              min="1"
              max="20"
              value={settings.halfTimeBreakDuration || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setSettings(prev => ({ ...prev, halfTimeBreakDuration: 0 }));
                } else {
                  const num = parseInt(value);
                  if (!isNaN(num) && num >= 0 && num <= 20) {
                    setSettings(prev => ({ ...prev, halfTimeBreakDuration: num }));
                  }
                }
              }}
              onBlur={() => {
                if (!settings.halfTimeBreakDuration || settings.halfTimeBreakDuration < 1) {
                  setSettings(prev => ({ ...prev, halfTimeBreakDuration: 5 }));
                }
              }}
              placeholder="Enter break duration"
              aria-label="Half-time break duration in minutes"
            />
          </div>

          {/* Notification Type */}
          <div className="space-y-2">
            <Label htmlFor="notification-type">Notification Sound</Label>
            <Select 
              value={settings.notificationType} 
              onValueChange={(value: 'siren' | 'beep' | 'alert') => 
                setSettings(prev => ({ ...prev, notificationType: value }))
              }
            >
              <SelectTrigger id="notification-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="siren">Bell (Ding-Dong)</SelectItem>
                <SelectItem value="beep">Radar (Pulsing Tone)</SelectItem>
                <SelectItem value="alert">Melody (Musical Phrase)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Choose the sound that plays when each half ends
            </p>
          </div>

          {/* Test Notification */}
          <div className="space-y-2">
            <Label>Notification Test</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Adjust your device volume to ensure you can hear the notification during games.
              Note: Vibration is not supported on iOS devices.
            </p>
            <Button 
              onClick={handleTestNotification} 
              variant="outline" 
              className="w-full"
              aria-label="Test notification"
            >
              <Bell className="mr-2 h-4 w-4" />
              Test Notification
            </Button>
          </div>

          {/* Theme Selection */}
          <div className="space-y-2">
            <Label>App Theme</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSettings(prev => ({ ...prev, theme: 'green' }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.theme === 'green' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border bg-card'
                }`}
                aria-label="Green theme"
                aria-pressed={settings.theme === 'green'}
              >
                <div className="w-full h-12 rounded-md bg-gradient-to-br from-green-800 to-green-900 mb-2" />
                <span className="text-sm font-medium">Green Pitch</span>
              </button>
              <button
                type="button"
                onClick={() => setSettings(prev => ({ ...prev, theme: 'black' }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.theme === 'black' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border bg-card'
                }`}
                aria-label="Black theme"
                aria-pressed={settings.theme === 'black'}
              >
                <div className="w-full h-12 rounded-md bg-black mb-2" />
                <span className="text-sm font-medium">Black</span>
              </button>
            </div>
          </div>

          {/* Team Names */}
          <div className="space-y-2">
            <Label htmlFor="home-team">Home Team Name</Label>
            <Input
              id="home-team"
              type="text"
              value={settings.homeTeamName}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                homeTeamName: e.target.value
              }))}
              aria-label="Home team name"
            />
          </div>

          <div className="space-y-2">
            <Label>Home Team Color</Label>
            {selectedHomeColor && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectedHomeColor.name}
              </p>
            )}
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" role="radiogroup" aria-label="Home team color">
              {TEAM_COLORS.map(color => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    homeTeamColor: color.value
                  }))}
                  className="w-12 h-12 rounded-md border-2 border-border hover:scale-110 transition-transform relative"
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                  aria-label={color.name}
                  role="radio"
                  aria-checked={settings.homeTeamColor === color.value}
                >
                  {settings.homeTeamColor === color.value && (
                    <Check className="h-5 w-5 text-white absolute inset-0 m-auto drop-shadow-lg" strokeWidth={3} />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="away-team">Away Team Name</Label>
            <Input
              id="away-team"
              type="text"
              value={settings.awayTeamName}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                awayTeamName: e.target.value
              }))}
              aria-label="Away team name"
            />
          </div>

          <div className="space-y-2">
            <Label>Away Team Color</Label>
            {selectedAwayColor && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectedAwayColor.name}
              </p>
            )}
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" role="radiogroup" aria-label="Away team color">
              {TEAM_COLORS.map(color => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    awayTeamColor: color.value
                  }))}
                  className="w-12 h-12 rounded-md border-2 border-border hover:scale-110 transition-transform relative"
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                  aria-label={color.name}
                  role="radio"
                  aria-checked={settings.awayTeamColor === color.value}
                >
                  {settings.awayTeamColor === color.value && (
                    <Check className="h-5 w-5 text-white absolute inset-0 m-auto drop-shadow-lg" strokeWidth={3} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <Button onClick={saveSettings} className="w-full" size="lg">
            Save Settings
          </Button>
        </Card>
      </div>
    </div>
  );
}

export default SettingsView;